/**
 * Supabase persistence for the AI explorer: chat history + saved "custom tables".
 * Written under the user's session — RLS enforces ownership/visibility.
 */
import { supabase } from '@/lib/supabase/client'
import type { AiQueryResult, VegaLiteSpec } from './ai-explore-api'
import type { AiColumnConfig } from '../lib/ai-result-adapter'

export interface AiSavedView {
  id: string
  project_id: string
  owner_id: string
  conversation_id: string | null
  title: string
  prompt: string | null
  sql: string
  dialect: string
  /** sanitized-on-render Vega-Lite spec, or null for a tabular result. */
  chart: VegaLiteSpec | null
  columns: string[] | null
  /** owner's per-view column customization (labels / hidden / derived). */
  column_config: AiColumnConfig
  visibility: 'private' | 'project'
  /** true while the view is a conversation's working draft (hidden from the sidebar). */
  is_draft: boolean
  created_at: string
}

export interface AiConversation {
  id: string
  project_id: string
  owner_id: string
  title: string
  draft_view_id: string | null
  created_at: string
  updated_at: string
}

export interface AiMessage {
  id: string
  conversation_id: string
  role: 'user' | 'assistant'
  content: string
  sql: string | null
  dialect: string | null
  columns: string[] | null
  chart: VegaLiteSpec | null
  row_count: number | null
  error: string | null
  created_at: string
}

export interface AiSavedChart {
  id: string
  project_id: string
  owner_id: string
  conversation_id: string | null
  title: string
  description: string | null
  vega_lite: VegaLiteSpec
  source_view_id: string | null
  source_sql: string | null
  dialect: string
  visibility: 'private' | 'project'
  created_at: string
}

export async function createConversation(projectId: string, ownerId: string, title: string): Promise<string> {
  const { data, error } = await supabase
    .from('ai_conversations')
    .insert({ project_id: projectId, owner_id: ownerId, title: title.slice(0, 120) })
    .select('id')
    .single()
  if (error) throw error
  return data.id
}

export async function appendMessages(
  conversationId: string,
  userPrompt: string,
  result: AiQueryResult,
): Promise<void> {
  const rows = [
    { conversation_id: conversationId, role: 'user', content: userPrompt },
    {
      conversation_id: conversationId,
      role: 'assistant',
      content: result.explanation ?? result.assistant_message ?? result.title ?? '',
      sql: result.sql ?? null,
      dialect: result.dialect ?? null,
      columns: (result.columns ?? null) as never,
      chart: (result.vega_lite ?? null) as never,
      row_count: result.row_count ?? null,
      error: result.ok ? null : result.message ?? 'error',
    },
  ]
  const { error } = await supabase.from('ai_messages').insert(rows)
  if (error) throw error
}

/** Auto-save a successful result as a sidebar "custom table". */
export async function saveGeneratedView(
  projectId: string,
  ownerId: string,
  conversationId: string | null,
  prompt: string,
  result: AiQueryResult,
): Promise<string> {
  const { data, error } = await supabase
    .from('ai_saved_views')
    .insert({
      project_id: projectId,
      owner_id: ownerId,
      conversation_id: conversationId,
      title: result.title ?? prompt.slice(0, 80),
      prompt,
      sql: result.sql!,
      dialect: result.dialect ?? 'postgres',
      chart: (result.vega_lite ?? null),
      columns: (result.columns ?? null),
    })
    .select('id')
    .single()
  if (error) throw error
  return data.id
}

/** Finalized saved views only (drafts are hidden until «نهایی‌کردن»). */
export async function fetchSavedViews(projectId: string): Promise<AiSavedView[]> {
  const { data, error } = await supabase
    .from('ai_saved_views')
    .select('*')
    .eq('project_id', projectId)
    .eq('is_deleted', false)
    .eq('is_draft', false)
    .order('created_at', { ascending: false })
  if (error) throw error
  return (data ?? []) as unknown as AiSavedView[]
}

export async function softDeleteSavedView(id: string): Promise<void> {
  const { error } = await supabase.from('ai_saved_views').update({ is_deleted: true }).eq('id', id)
  if (error) throw error
}

export async function setSavedViewVisibility(id: string, visibility: 'private' | 'project'): Promise<void> {
  const { error } = await supabase.from('ai_saved_views').update({ visibility }).eq('id', id)
  if (error) throw error
}

/** Persist the owner's column customization (labels / hidden / derived columns). */
export async function updateSavedViewColumns(id: string, config: AiColumnConfig): Promise<void> {
  const { error } = await supabase.from('ai_saved_views').update({ column_config: config }).eq('id', id)
  if (error) throw error
}

// ─── Conversations (history + resume) ──────────────────────────────────────────

export async function listConversations(projectId: string): Promise<AiConversation[]> {
  const { data, error } = await supabase
    .from('ai_conversations')
    .select('*')
    .eq('project_id', projectId)
    .eq('is_deleted', false)
    .order('updated_at', { ascending: false })
  if (error) throw error
  return (data ?? []) as unknown as AiConversation[]
}

export async function getConversationMessages(conversationId: string): Promise<AiMessage[]> {
  const { data, error } = await supabase
    .from('ai_messages')
    .select('*')
    .eq('conversation_id', conversationId)
    .order('created_at', { ascending: true })
  if (error) throw error
  return (data ?? []) as unknown as AiMessage[]
}

export async function renameConversation(id: string, title: string): Promise<void> {
  const { error } = await supabase.from('ai_conversations').update({ title: title.slice(0, 120) }).eq('id', id)
  if (error) throw error
}

export async function softDeleteConversation(id: string): Promise<void> {
  const { error } = await supabase.from('ai_conversations').update({ is_deleted: true }).eq('id', id)
  if (error) throw error
}

export async function touchConversation(id: string): Promise<void> {
  // bump updated_at so the conversation sorts to the top of the history list.
  const { error } = await supabase.from('ai_conversations').update({ updated_at: new Date().toISOString() }).eq('id', id)
  if (error) throw error
}

// ─── Draft view (one working view per conversation) ─────────────────────────────

/** Create the conversation's working DRAFT view (is_draft=true) from a result. */
export async function createDraftView(
  projectId: string, ownerId: string, conversationId: string, prompt: string, result: AiQueryResult,
): Promise<string> {
  const { data, error } = await supabase
    .from('ai_saved_views')
    .insert({
      project_id: projectId, owner_id: ownerId, conversation_id: conversationId,
      title: result.title ?? prompt.slice(0, 80), prompt,
      sql: result.sql!, dialect: result.dialect ?? 'postgres',
      chart: (result.vega_lite ?? null), columns: (result.columns ?? null),
      is_draft: true,
    })
    .select('id').single()
  if (error) throw error
  const id = (data as { id: string }).id
  await supabase.from('ai_conversations').update({ draft_view_id: id }).eq('id', conversationId)
  return id
}

/** Update the draft in place after an edit turn (no new row). */
export async function updateDraftView(
  id: string,
  patch: { title?: string; prompt?: string; sql?: string; dialect?: string; chart?: VegaLiteSpec | null; columns?: string[] | null },
): Promise<void> {
  const update: Record<string, unknown> = {}
  if (patch.title !== undefined) update.title = patch.title
  if (patch.prompt !== undefined) update.prompt = patch.prompt
  if (patch.sql !== undefined) update.sql = patch.sql
  if (patch.dialect !== undefined) update.dialect = patch.dialect
  if (patch.chart !== undefined) update.chart = patch.chart
  if (patch.columns !== undefined) update.columns = patch.columns
  const { error } = await supabase.from('ai_saved_views').update(update).eq('id', id)
  if (error) throw error
}

export async function getView(id: string): Promise<AiSavedView | null> {
  const { data, error } = await supabase.from('ai_saved_views').select('*').eq('id', id).maybeSingle()
  if (error) throw error
  return (data as unknown as AiSavedView) ?? null
}

/** Promote a draft to a reusable finalized view. */
export async function finalizeView(id: string, title?: string): Promise<void> {
  const update: Record<string, unknown> = { is_draft: false }
  if (title) update.title = title
  const { error } = await supabase.from('ai_saved_views').update(update).eq('id', id)
  if (error) throw error
}

// ─── Saved charts (reusable artifact) ───────────────────────────────────────────

export async function createChart(
  projectId: string, ownerId: string,
  input: { conversationId?: string | null; title: string; description?: string; vegaLite: VegaLiteSpec; sourceViewId?: string | null; sourceSql?: string | null; dialect?: string },
): Promise<string> {
  const { data, error } = await supabase
    .from('ai_saved_charts')
    .insert({
      project_id: projectId, owner_id: ownerId, conversation_id: input.conversationId ?? null,
      title: input.title, description: input.description ?? null,
      vega_lite: input.vegaLite,
      source_view_id: input.sourceViewId ?? null, source_sql: input.sourceSql ?? null,
      dialect: input.dialect ?? 'postgres',
    })
    .select('id').single()
  if (error) throw error
  return (data as { id: string }).id
}

export async function listCharts(projectId: string): Promise<AiSavedChart[]> {
  const { data, error } = await supabase
    .from('ai_saved_charts')
    .select('*')
    .eq('project_id', projectId)
    .eq('is_deleted', false)
    .order('created_at', { ascending: false })
  if (error) throw error
  return (data ?? []) as unknown as AiSavedChart[]
}

export async function softDeleteChart(id: string): Promise<void> {
  const { error } = await supabase.from('ai_saved_charts').update({ is_deleted: true }).eq('id', id)
  if (error) throw error
}
